/**
 * Dynamic drawer transition helper. It delays the open transform until after
 * mount so drawers animate from offscreen instead of snapping into place.
 */
import { ref, watch, onBeforeUnmount } from 'vue';
import { DrawerSide } from '@/store/modules/dynamicModals/types.ts';

export const DRAWER_TRANSITION_DURATION_MS = 300;

export interface DrawerTransitionEntry {
    id: string;
    drawerSide: DrawerSide;
}

const closedTransformBySide: Record<DrawerSide, string> = {
    [DrawerSide.Right]: 'translateX(0)',
    [DrawerSide.Left]: 'translateX(-100%)',
};

const openTransformBySide: Record<DrawerSide, string> = {
    [DrawerSide.Right]: 'translateX(-100%)',
    [DrawerSide.Left]: 'translateX(0)',
};

export const getDrawerClosedTransform = (side: DrawerSide) => closedTransformBySide[side];

export const getDrawerOpenTransform = (side: DrawerSide) => openTransformBySide[side];

const scheduleAfterTwoAnimationFrames = (callback: () => void) => {
    const frameIds: number[] = [];
    const firstFrameId = globalThis.requestAnimationFrame(() => {
        const secondFrameId = globalThis.requestAnimationFrame(callback);
        frameIds.push(secondFrameId);
    });
    frameIds.push(firstFrameId);
    return frameIds;
};

export default function useDrawerTransitions(getEntries: () => DrawerTransitionEntry[]) {
    const visibleDrawerIds = ref<Set<string>>(new Set());
    const drawerOpenFrameIds = new Map<string, number[]>();
    const drawerCleanupTimeoutIds = new Map<string, number>();

    const isDrawerOpen = (id: string) => visibleDrawerIds.value.has(id);

    const getDrawerTransform = (id: string, side: DrawerSide) => {
        return isDrawerOpen(id) ? getDrawerOpenTransform(side) : getDrawerClosedTransform(side);
    };

    const hasDrawerEntry = (id: string) => getEntries().some((drawer) => drawer.id === id);

    const setDrawerVisible = (id: string, visible: boolean) => {
        const nextVisibleDrawerIds = new Set(visibleDrawerIds.value);
        if (visible) {
            nextVisibleDrawerIds.add(id);
        } else {
            nextVisibleDrawerIds.delete(id);
        }
        visibleDrawerIds.value = nextVisibleDrawerIds;
    };

    const clearDrawerOpenSchedule = (id: string) => {
        const frameIds = drawerOpenFrameIds.get(id);
        if (!frameIds || globalThis.window === undefined) {
            return;
        }

        frameIds.forEach((frameId) => globalThis.cancelAnimationFrame(frameId));
        drawerOpenFrameIds.delete(id);
    };

    const finishDrawerOpen = (id: string) => {
        drawerOpenFrameIds.delete(id);
        if (hasDrawerEntry(id)) {
            setDrawerVisible(id, true);
        }
    };

    const scheduleDrawerOpen = (id: string) => {
        if (visibleDrawerIds.value.has(id) || drawerOpenFrameIds.has(id)) {
            return;
        }

        if (globalThis.window === undefined) {
            setDrawerVisible(id, true);
            return;
        }

        const frameIds = scheduleAfterTwoAnimationFrames(() => finishDrawerOpen(id));
        drawerOpenFrameIds.set(id, frameIds);
        if (visibleDrawerIds.value.has(id)) {
            drawerOpenFrameIds.delete(id);
        }
    };

    const scheduleDrawerVisibilityCleanup = (id: string) => {
        if (drawerCleanupTimeoutIds.has(id)) {
            return;
        }

        if (globalThis.window === undefined) {
            setDrawerVisible(id, false);
            return;
        }

        const timeoutId = globalThis.setTimeout(() => {
            setDrawerVisible(id, false);
            drawerCleanupTimeoutIds.delete(id);
        }, DRAWER_TRANSITION_DURATION_MS);
        drawerCleanupTimeoutIds.set(id, timeoutId);
    };

    watch(
        () => getEntries().map((drawer) => drawer.id),
        (drawerIds) => {
            const currentDrawerIds = new Set(drawerIds);

            drawerIds.forEach((id) => {
                const cleanupTimeoutId = drawerCleanupTimeoutIds.get(id);
                if (cleanupTimeoutId && globalThis.window !== undefined) {
                    globalThis.clearTimeout(cleanupTimeoutId);
                    drawerCleanupTimeoutIds.delete(id);
                }

                scheduleDrawerOpen(id);
            });

            drawerOpenFrameIds.forEach((_frameIds, id) => {
                if (!currentDrawerIds.has(id)) {
                    clearDrawerOpenSchedule(id);
                }
            });

            visibleDrawerIds.value.forEach((id) => {
                if (!currentDrawerIds.has(id)) {
                    scheduleDrawerVisibilityCleanup(id);
                }
            });
        },
        { immediate: true }
    );

    onBeforeUnmount(() => {
        if (globalThis.window === undefined) {
            return;
        }

        drawerOpenFrameIds.forEach((_frameIds, id) => clearDrawerOpenSchedule(id));
        drawerCleanupTimeoutIds.forEach((timeoutId) => globalThis.clearTimeout(timeoutId));
        drawerCleanupTimeoutIds.clear();
    });

    return {
        visibleDrawerIds,
        isDrawerOpen,
        getDrawerTransform,
    };
}
